/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CompatibleBrandsBox.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Box component showing compatible phone brands list with searchable listbox
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useRef, useEffect } from 'react';
import { Phone, Search, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PhonebookFormatDemoModal from './PhonebookFormatDemoModal';
import type { PhoneBrand } from './types';

// List of compatible phone brands with display names
export const COMPATIBLE_BRANDS: Array<{ brand: PhoneBrand; name: string }> = [
  { brand: 'yealink', name: 'Yealink' },
  { brand: 'grandstream', name: 'Grandstream' },
  { brand: 'polycom', name: 'Polycom' },
  { brand: 'cisco', name: 'Cisco' },
  { brand: 'panasonic', name: 'Panasonic' },
  { brand: 'avaya', name: 'Avaya' },
  { brand: 'mitel', name: 'Mitel' },
  { brand: 'snom', name: 'Snom' },
  { brand: 'aastra', name: 'Aastra' },
  { brand: 'fanvil', name: 'Fanvil' },
  { brand: 'dlink', name: 'D-Link' },
  { brand: 'nec', name: 'NEC' },
  { brand: 'siemens', name: 'Siemens' },
  { brand: 'lg', name: 'LG' },
  { brand: 'htek', name: 'Htek' },
  { brand: 'vtech', name: 'VTech' },
  { brand: 'alcatel', name: 'Alcatel-Lucent' },
  { brand: 'gigaset', name: 'Gigaset' },
  { brand: 'sangoma', name: 'Sangoma' },
  { brand: 'escene', name: 'Escene' },
  { brand: 'linksys', name: 'Linksys' },
  { brand: 'zyxel', name: 'Zyxel' },
  { brand: 'unify', name: 'Unify' },
  { brand: 'switchboard', name: 'Switchboard (JSON)' },
  { brand: 'generic', name: 'Genérico (XML estándar)' },
];

export default function CompatibleBrandsBox() {
  const { t } = useI18n();
  const [selectedBrand, setSelectedBrand] = useState<PhoneBrand | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) {
      return COMPATIBLE_BRANDS;
    }
    const query = searchQuery.toLowerCase();
    return COMPATIBLE_BRANDS.filter(({ name }) =>
      name.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const selectedBrandName = useMemo(() => {
    if (!selectedBrand) return null;
    return COMPATIBLE_BRANDS.find((b) => b.brand === selectedBrand)?.name || selectedBrand;
  }, [selectedBrand]);

  const handleViewFormat = () => {
    if (selectedBrand) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelectBrand = (brand: PhoneBrand) => {
    setSelectedBrand(brand);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <>
      <FormBox title={t('phonebooks.compatibleBrands.title', 'Marcas Compatibles')}>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('phonebooks.compatibleBrands.description', 'El sistema detecta automáticamente el modelo de teléfono y devuelve el formato XML correcto. Seleccione una marca para ver un ejemplo del formato.')}
          </p>

          <div className="flex items-end gap-3">
            {/* Listbox Container */}
            <div ref={containerRef} className="flex-1 relative">
              <Label htmlFor="brand-select" className="text-sm mb-1 block">
                {t('phonebooks.compatibleBrands.selectBrand', 'Seleccionar Marca')}
              </Label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-full px-3 py-2 text-left bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/60 flex items-center justify-between hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className={selectedBrandName ? 'text-foreground' : 'text-muted-foreground truncate'}>
                      {selectedBrandName || t('phonebooks.compatibleBrands.placeholder', 'Seleccione una marca...')}
                    </span>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </button>

                {/* Dropdown */}
                {isOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-border">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          ref={inputRef}
                          type="text"
                          placeholder={t('phonebooks.compatibleBrands.searchPlaceholder', 'Buscar marca...')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto">
                      {filteredBrands.length > 0 ? (
                        filteredBrands.map(({ brand, name }) => (
                          <button
                            key={brand}
                            type="button"
                            onClick={() => handleSelectBrand(brand)}
                            className={`w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground flex items-center gap-2 transition-colors ${selectedBrand === brand ? 'bg-accent text-accent-foreground' : ''
                              }`}
                          >
                            <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm">{name}</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                          {t('phonebooks.compatibleBrands.noResults', 'No se encontraron marcas')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* View Format Button */}
            <Button
              type="button"
              onClick={handleViewFormat}
              disabled={!selectedBrand}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              {t('phonebooks.compatibleBrands.viewFormat', 'Ver Formato')}
            </Button>
          </div>
        </div>
      </FormBox>

      {selectedBrand && (
        <PhonebookFormatDemoModal
          isOpen={showModal}
          onClose={handleCloseModal}
          brand={selectedBrand}
        />
      )}
    </>
  );
}

